import {
  Box,
  Grid,
  Heading,
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Flex,
  Input,
  FormControl,
  FormLabel,
  Select,
  HStack,
  Icon,
} from '@chakra-ui/react'
import {
  LineChart as RechartsLineChart,
  BarChart as RechartsBarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Calendar } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '@/lib/api/client'
import { AdminLayout } from '@/components/layout/AdminLayout'
import {
  dashboardService,
  HelpdeskDashboardStats,
} from '@/lib/services/dashboardService'
import {
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

type SummaryCardKey = 'resolution_rate' | 'incident_sla' | 'request_sla' | 'open_tickets'

const SUMMARY_CARDS: { key: SummaryCardKey; label: string; valueType: 'percent' | 'number' }[] = [
  { key: 'resolution_rate', label: 'Resolution Rate', valueType: 'percent' },
  { key: 'incident_sla', label: 'Incident SLA', valueType: 'percent' },
  { key: 'request_sla', label: 'Request SLA', valueType: 'percent' },
  { key: 'open_tickets', label: 'Open Tickets', valueType: 'number' },
]

const defaultTicketStats: HelpdeskDashboardStats = {
  unresolved: 0,
  overdue: 0,
  due_today: 0,
  open: 0,
  on_hold: 0,
  unassigned: 0,
  resolved: 0,
  received: 0,
  net_recovery_minutes: 0,
  resolution_within_sla_percent: 0,
  trends_today: [],
  trends_yesterday: [],
  kpi_open: 0,
  kpi_in_progress: 0,
  kpi_pending: 0,
  kpi_on_hold: 0,
  kpi_closed: 0,
  kpi_cancelled: 0,
  ticket_source: { period: 'last_7_days', total_active: 0, sources: [] },
  agent_performance: [],
  sla_breach_total: 0,
  sla_breach_by_source: [],
}

function formatMTTR(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return '—'
  const totalMinutes = minutes
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  if (hours > 0) {
    return `${hours}h ${mins.toFixed(2)}m`
  }
  return `${mins.toFixed(2)}m`
}

type ServiceTypeOption = { id: number; name: string; code: string }

type DivisionChartRow = { division: string; incident: number; service: number; request: number }
type ResolutionSlaRow = { month: string; withinSla: number; nearingSla: number; breached: number }
type TrendByTypeRow = { label: string; incident: number; service: number; request: number }
type MttrCategoryRow = { category: string; under4h: number; from4hTo1d: number; over1d: number }
type SubCategoryRow = { subCategory: string; service: number; incident: number; request: number }
type DateRangeGranularity = 'weekly' | 'monthly' | 'custom'
type ResolutionServiceTypeFilter = 'all' | 'incident' | 'inquiry' | 'request'

type DashboardChartPayload = {
  ticketDistribution?: unknown
  resolutionByTypeSla?: unknown
  ticketResolutionTrend?: unknown
  ticketTrendByWeekType?: unknown
  mttrByCategory?: unknown
  itemsPerSubCategory?: unknown
}

const EMPTY_CHARTS_PAYLOAD: DashboardChartPayload = {
  ticketDistribution: [],
  resolutionByTypeSla: [],
  ticketResolutionTrend: { labels: [], current: [], previous: [] },
  ticketTrendByWeekType: [],
  mttrByCategory: [],
  itemsPerSubCategory: [],
}

const TIGHT_BAR_CHART_PROPS = {
  barCategoryGap: '28%',
  barGap: 4,
  maxBarSize: 24,
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function getChartRows(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is Record<string, unknown> => !!asRecord(item))
  }

  const record = asRecord(value)
  if (!record) return []
  const nested = record.data
  if (Array.isArray(nested)) {
    return nested.filter((item): item is Record<string, unknown> => !!asRecord(item))
  }

  return []
}

function toSafeNumber(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function toSafeLabel(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value))
}

function getSourceCountByName(
  items: Array<{ name: string; value: number }> | undefined,
  keyword: 'incident' | 'request',
): number {
  if (!items?.length) return 0
  const match = items.find((item) => item.name.toLowerCase().includes(keyword))
  return match?.value ?? 0
}

function deriveSlaPercentByType(
  stats: HelpdeskDashboardStats,
  keyword: 'incident' | 'request',
): number {
  const total = getSourceCountByName(stats.ticket_source?.sources, keyword)
  const breached = getSourceCountByName(stats.sla_breach_by_source, keyword)
  if (total <= 0) return clampPercent(stats.resolution_within_sla_percent || 0)
  return clampPercent(((total - breached) / total) * 100)
}

function formatStatCardValue(value: number, valueType: 'percent' | 'number'): string {
  if (valueType === 'percent') return `${clampPercent(value).toFixed(1)}%`
  return Math.max(0, Math.round(value)).toLocaleString()
}

function buildDivisionChartData(
  _ticketStats: HelpdeskDashboardStats,
  _period: 'weekly' | 'monthly',
  rawPayload?: unknown,
): DivisionChartRow[] {
  const mapped = getChartRows(rawPayload).map((row, index) => {
    return {
      division: toSafeLabel(row.division ?? row.name ?? row.label, `Division ${index + 1}`),
      incident: Math.max(toSafeNumber(row.incident), 0),
      service: Math.max(toSafeNumber(row.service), 0),
      request: Math.max(toSafeNumber(row.request), 0),
    }
  })
  return mapped
}

function buildResolutionByTypeSlaData(
  _ticketStats: HelpdeskDashboardStats,
  _selectedServiceTypeFilter: ResolutionServiceTypeFilter,
  _dateRangeGranularity: DateRangeGranularity,
  _startDate?: string,
  _endDate?: string,
  rawPayload?: unknown,
): ResolutionSlaRow[] {
  const mapped = getChartRows(rawPayload).map((row, index) => ({
    month: toSafeLabel(row.label ?? row.month ?? row.period, `M${index + 1}`),
    withinSla: Math.max(toSafeNumber(row.within_sla ?? row.withinSla), 0),
    nearingSla: Math.max(toSafeNumber(row.nearing_sla ?? row.nearingSla), 0),
    breached: Math.max(toSafeNumber(row.breached ?? row.breach), 0),
  }))
  return mapped
}

function buildResolutionTrendData(
  _ticketStats: HelpdeskDashboardStats,
  _dateRangeGranularity: DateRangeGranularity,
  _startDate?: string,
  _endDate?: string,
  rawPayload?: unknown,
): Array<{ label: string; current: number; previous: number }> {
  const record = asRecord(rawPayload)
  const labels = Array.isArray(record?.labels) ? record.labels : []
  const current = Array.isArray(record?.current) ? record.current : []
  const previous = Array.isArray(record?.previous) ? record.previous : []
  return labels.map((label, index) => ({
    label: toSafeLabel(label, `P${index + 1}`),
    current: Math.max(toSafeNumber(current[index]), 0),
    previous: Math.max(toSafeNumber(previous[index]), 0),
  }))
}

function buildTicketTrendByTypeData(
  _ticketStats: HelpdeskDashboardStats,
  _dateRangeGranularity: DateRangeGranularity,
  _startDate?: string,
  _endDate?: string,
  rawPayload?: unknown,
): TrendByTypeRow[] {
  const mapped = getChartRows(rawPayload).map((row, index) => ({
    label: toSafeLabel(row.week ?? row.label ?? row.month ?? row.period, `T${index + 1}`),
    incident: Math.max(toSafeNumber(row.incident), 0),
    service: Math.max(toSafeNumber(row.service), 0),
    request: Math.max(toSafeNumber(row.request), 0),
  }))
  return mapped
}

function buildMttrByCategoryData(
  _ticketStats: HelpdeskDashboardStats,
  selectedServiceTypeId: number | null,
  _serviceTypes: ServiceTypeOption[],
  _dateRangeGranularity: DateRangeGranularity,
  _startDate?: string,
  _endDate?: string,
  rawPayload?: unknown,
): MttrCategoryRow[] {
  const mapped = getChartRows(rawPayload).map((row, index) => ({
    category: toSafeLabel(row.category ?? row.type ?? row.label, `Category ${index + 1}`),
    under4h: Math.max(toSafeNumber(row.under_4h ?? row.under4h), 0),
    from4hTo1d: Math.max(toSafeNumber(row.from_4h_to_1d ?? row.from4hTo1d), 0),
    over1d: Math.max(toSafeNumber(row.over_1d ?? row.over1d), 0),
    serviceTypeId: toSafeNumber(
      row.service_type_id ?? row.serviceTypeId ?? asRecord(row.serviceType)?.id,
      0,
    ),
  }))
  if (!selectedServiceTypeId) {
    return mapped.map(({ category, under4h, from4hTo1d, over1d }) => ({
      category,
      under4h,
      from4hTo1d,
      over1d,
    }))
  }

  return mapped
    .filter((row) => row.serviceTypeId === selectedServiceTypeId)
    .map(({ category, under4h, from4hTo1d, over1d }) => ({
      category,
      under4h,
      from4hTo1d,
      over1d,
    }))
}

function buildItemsPerSubCategoryData(
  _dateRangeGranularity: DateRangeGranularity,
  filter: 'all' | 'service' | 'incident' | 'request',
  _startDate?: string,
  _endDate?: string,
  rawPayload?: unknown,
): SubCategoryRow[] {
  const mapped = getChartRows(rawPayload).map((row, index) => ({
    subCategory: toSafeLabel(row.sub_category ?? row.subCategory ?? row.label, `Subcategory ${index + 1}`),
    service: Math.max(toSafeNumber(row.service), 0),
    incident: Math.max(toSafeNumber(row.incident), 0),
    request: Math.max(toSafeNumber(row.request), 0),
  }))

  if (filter === 'all') return mapped
  return mapped.map((row) => ({
    subCategory: row.subCategory,
    service: filter === 'service' ? row.service : 0,
    incident: filter === 'incident' ? row.incident : 0,
    request: filter === 'request' ? row.request : 0,
  }))
}

function formatDateForInput(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function formatMonthForInput(d: Date): string {
  return format(d, 'yyyy-MM')
}

function parseFilterAnchorDate(value: string, granularity: DateRangeGranularity): Date {
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

function formatDisplayedDateRange(value: string, granularity: DateRangeGranularity): string {
  const anchorDate = parseFilterAnchorDate(value, granularity)
  const startDate = granularity === 'monthly'
    ? startOfMonth(anchorDate)
    : startOfWeek(anchorDate, { weekStartsOn: 1 })
  const endDate = granularity === 'monthly'
    ? endOfMonth(anchorDate)
    : endOfWeek(anchorDate, { weekStartsOn: 1 })
  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
}

function formatVisibleDateValue(value: string): string {
  if (!value) return ''
  const parsed = parseISO(value)
  if (Number.isNaN(parsed.getTime())) return value
  return format(parsed, 'MMM d, yyyy')
}

function getDefaultDashboardRange(): { start_date: string; end_date: string } {
  const now = new Date()
  const start = startOfMonth(now)
  const end = endOfMonth(now)
  return { start_date: formatDateForInput(start), end_date: formatDateForInput(end) }
}

export default function DashboardPage() {
  const defaultRange = useMemo(() => getDefaultDashboardRange(), [])
  const [ticketStats, setTicketStats] = useState<HelpdeskDashboardStats>(defaultTicketStats)
  const [isLoading, setIsLoading] = useState(true)
  const [chartsPayload, setChartsPayload] = useState<DashboardChartPayload>(EMPTY_CHARTS_PAYLOAD)
  const [resolutionByTypeSlaPayload, setResolutionByTypeSlaPayload] = useState<unknown>([])
  const [mttrByCategoryPayload, setMttrByCategoryPayload] = useState<unknown>([])
  const [filters, setFilters] = useState<{
    start_date: string
    end_date: string
    service_type_id: number | null
    ticket_status_id: number | null
  }>({
    ...defaultRange,
    service_type_id: null,
    ticket_status_id: null,
  })
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([])
  const [dateRangeGranularity, setDateRangeGranularity] = useState<DateRangeGranularity>('monthly')
  const [selectedDateValue, setSelectedDateValue] = useState<string>(defaultRange.end_date)
  const [customDateFrom, setCustomDateFrom] = useState<string>(defaultRange.start_date)
  const [customDateTo, setCustomDateTo] = useState<string>(defaultRange.end_date)
  const [resolutionServiceTypeFilter, setResolutionServiceTypeFilter] = useState<ResolutionServiceTypeFilter>('all')
  const [mttrServiceTypeId, setMttrServiceTypeId] = useState<number | null>(null)
  const [subCategoryFilter, setSubCategoryFilter] = useState<'all' | 'service' | 'incident' | 'request'>('all')
  const previousRangeGranularityRef = useRef<DateRangeGranularity>(dateRangeGranularity)
  const latestFetchIdRef = useRef(0)
  const datePickerInputRef = useRef<HTMLInputElement | null>(null)
  const customFromInputRef = useRef<HTMLInputElement | null>(null)
  const customToInputRef = useRef<HTMLInputElement | null>(null)

  const openPicker = useCallback((input: HTMLInputElement | null) => {
    if (!input) return

    input.focus()
    if (typeof input.showPicker === 'function') {
      input.showPicker()
      return
    }

    input.click()
  }, [])

  const openDatePicker = useCallback(() => {
    openPicker(datePickerInputRef.current)
  }, [openPicker])

  const openCustomFromPicker = useCallback(() => {
    openPicker(customFromInputRef.current)
  }, [openPicker])

  const openCustomToPicker = useCallback(() => {
    openPicker(customToInputRef.current)
  }, [openPicker])

  useEffect(() => {
    apiClient.get('/service-catalog/service-types', { params: { per_page: 200 } }).then((res) => {
      const list = Array.isArray(res.data) ? res.data : res.data?.data ?? []
      setServiceTypes(list.map((s: ServiceTypeOption) => ({ id: s.id, name: s.name, code: s.code })))
    }).catch(() => {})
  }, [])

  const fetchDashboardData = useCallback(async () => {
    const fetchId = ++latestFetchIdRef.current
    try {
      const chartParams = {
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        range_type: dateRangeGranularity,
        ticket_status_id: filters.ticket_status_id,
      } as const

      const [ticketData, baseCharts, resolutionCharts, mttrCharts] = await Promise.all([
        dashboardService.getTicketStats({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          service_type_id: filters.service_type_id,
          ticket_status_id: filters.ticket_status_id,
        }),
        dashboardService.getDashboardCharts({
          ...chartParams,
          service_type: 'all',
          service_type_id: filters.service_type_id,
        }),
        dashboardService.getDashboardCharts({
          ...chartParams,
          service_type: resolutionServiceTypeFilter,
          service_type_id: filters.service_type_id,
        }),
        dashboardService.getDashboardCharts({
          ...chartParams,
          service_type_id: mttrServiceTypeId,
        }),
      ])

      const basePayload: DashboardChartPayload = {
        ticketDistribution: baseCharts.ticketDistribution,
        resolutionByTypeSla: baseCharts.resolutionByTypeSla,
        ticketResolutionTrend: baseCharts.ticketResolutionTrend,
        ticketTrendByWeekType: baseCharts.ticketTrendByWeekType,
        mttrByCategory: baseCharts.mttrByCategory,
        itemsPerSubCategory: baseCharts.itemsPerSubCategory,
      }
      if (fetchId !== latestFetchIdRef.current) return
      setChartsPayload(basePayload)
      setResolutionByTypeSlaPayload(resolutionCharts.resolutionByTypeSla)
      setMttrByCategoryPayload(mttrCharts.mttrByCategory)

      // Merge base ticket stats
      const merged: HelpdeskDashboardStats = {
        ...defaultTicketStats,
        ...ticketData,
        net_recovery_minutes: ticketData.net_recovery_minutes ?? 0,
        trends_today: ticketData.trends_today ?? [],
        trends_yesterday: ticketData.trends_yesterday ?? [],
      }

      setTicketStats(merged)
    } catch (error) {
      if (fetchId !== latestFetchIdRef.current) return
      console.warn('Ticket stats not available:', error)
      setChartsPayload(EMPTY_CHARTS_PAYLOAD)
      setResolutionByTypeSlaPayload([])
      setMttrByCategoryPayload([])
    } finally {
      if (fetchId !== latestFetchIdRef.current) return
      setIsLoading(false)
    }
  }, [
    filters.start_date,
    filters.end_date,
    filters.service_type_id,
    filters.ticket_status_id,
    dateRangeGranularity,
    resolutionServiceTypeFilter,
    mttrServiceTypeId,
  ])

  useEffect(() => {
    setIsLoading(true)
    fetchDashboardData()
  }, [fetchDashboardData])

  useEffect(() => {
    if (dateRangeGranularity === 'custom') return
    const selectedDate = parseFilterAnchorDate(selectedDateValue, dateRangeGranularity)
    const startDate = dateRangeGranularity === 'monthly'
      ? startOfMonth(selectedDate)
      : startOfWeek(selectedDate, { weekStartsOn: 1 })
    const endDate = dateRangeGranularity === 'monthly'
      ? endOfMonth(selectedDate)
      : endOfWeek(selectedDate, { weekStartsOn: 1 })
    const nextStart = formatDateForInput(startDate)
    const nextEnd = formatDateForInput(endDate)
    setFilters((prev) => {
      if (prev.start_date === nextStart && prev.end_date === nextEnd) {
        return prev
      }
      return {
        ...prev,
        start_date: nextStart,
        end_date: nextEnd,
      }
    })
  }, [dateRangeGranularity, selectedDateValue])

  useEffect(() => {
    if (dateRangeGranularity !== 'custom') return
    if (!customDateFrom || !customDateTo) return
    setFilters((prev) => {
      if (prev.start_date === customDateFrom && prev.end_date === customDateTo) {
        return prev
      }
      return {
        ...prev,
        start_date: customDateFrom,
        end_date: customDateTo,
      }
    })
  }, [dateRangeGranularity, customDateFrom, customDateTo])

  useEffect(() => {
    const previous = previousRangeGranularityRef.current

    if (dateRangeGranularity === 'custom') {
      if (previous !== 'custom') {
        setCustomDateFrom(filters.start_date)
        setCustomDateTo(filters.end_date)
      }
      previousRangeGranularityRef.current = dateRangeGranularity
      return
    }

    if (previous === 'custom') {
      const anchorDate = parseFilterAnchorDate(selectedDateValue, dateRangeGranularity)
      setSelectedDateValue(
        formatDateForInput(anchorDate),
      )
    }

    previousRangeGranularityRef.current = dateRangeGranularity
  }, [dateRangeGranularity, filters.start_date, filters.end_date, selectedDateValue])

  const displayedDateRangeValue = useMemo(() => {
    if (dateRangeGranularity === 'custom') {
      return `${customDateFrom || '—'} - ${customDateTo || '—'}`
    }
    return formatDisplayedDateRange(selectedDateValue, dateRangeGranularity)
  }, [dateRangeGranularity, selectedDateValue, customDateFrom, customDateTo])

  const displayedDateInputWidth = useMemo(() => {
    // Size the control from current text so long ranges are not clipped.
    return `${Math.max(displayedDateRangeValue.length + 2, 24)}ch`
  }, [displayedDateRangeValue])

  const divisionData = useMemo(
    () => buildDivisionChartData(ticketStats, 'weekly', chartsPayload.ticketDistribution),
    [ticketStats, chartsPayload.ticketDistribution],
  )

  const resolutionByTypeSlaData = useMemo(
    () => buildResolutionByTypeSlaData(
      ticketStats,
      resolutionServiceTypeFilter,
      dateRangeGranularity,
      filters.start_date,
      filters.end_date,
      resolutionByTypeSlaPayload,
    ),
    [
      ticketStats,
      resolutionServiceTypeFilter,
      dateRangeGranularity,
      filters.start_date,
      filters.end_date,
      resolutionByTypeSlaPayload,
    ],
  )

  const ticketTrendByWeekTypeData = useMemo(
    () => buildTicketTrendByTypeData(
      ticketStats,
      dateRangeGranularity,
      filters.start_date,
      filters.end_date,
      chartsPayload.ticketTrendByWeekType,
    ),
    [ticketStats, dateRangeGranularity, filters.start_date, filters.end_date, chartsPayload.ticketTrendByWeekType],
  )

  const resolutionTrendData = useMemo(
    () => buildResolutionTrendData(
      ticketStats,
      dateRangeGranularity,
      filters.start_date,
      filters.end_date,
      chartsPayload.ticketResolutionTrend,
    ),
    [ticketStats, dateRangeGranularity, filters.start_date, filters.end_date, chartsPayload.ticketResolutionTrend],
  )

  const mttrByCategoryData = useMemo(
    () => buildMttrByCategoryData(
      ticketStats,
      mttrServiceTypeId,
      serviceTypes,
      dateRangeGranularity,
      filters.start_date,
      filters.end_date,
      mttrByCategoryPayload,
    ),
    [
      ticketStats,
      mttrServiceTypeId,
      serviceTypes,
      dateRangeGranularity,
      filters.start_date,
      filters.end_date,
      mttrByCategoryPayload,
    ],
  )

  const itemsPerSubCategoryData = useMemo(
    () => buildItemsPerSubCategoryData(
      dateRangeGranularity,
      subCategoryFilter,
      filters.start_date,
      filters.end_date,
      chartsPayload.itemsPerSubCategory,
    ),
    [dateRangeGranularity, subCategoryFilter, filters.start_date, filters.end_date, chartsPayload.itemsPerSubCategory],
  )

  const summaryCardValues = useMemo<Record<SummaryCardKey, number>>(() => {
    const resolved = Math.max(ticketStats.resolved ?? 0, 0)
    const received = Math.max(ticketStats.received ?? 0, 0)
    const resolutionRate = received > 0
      ? (resolved / received) * 100
      : clampPercent(ticketStats.resolution_within_sla_percent ?? 0)

    return {
      resolution_rate: resolutionRate,
      incident_sla: deriveSlaPercentByType(ticketStats, 'incident'),
      request_sla: deriveSlaPercentByType(ticketStats, 'request'),
      open_tickets: Math.max(ticketStats.open ?? 0, 0),
    }
  }, [ticketStats])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={4} align="stretch" flex="1" overflowY="auto" minH={0}>
          <Card>
            <CardHeader>
              <Heading size="md">Operations Dashboard</Heading>
              <Text fontSize="sm" color="gray.500">
                Ticket operations metrics and trends.
              </Text>
            </CardHeader>
          </Card>

          {/* Main Box A Holds the Following:
              Stat Cards
              Ticket Distribution per Division
              Date Range Filter
              Ticket Resolution per Type and SLA
              Ticket Resolution Trend
              */}
          {/* Extra information: If the filter is set to monthly, then only the 
              Selected month will be displayed. If the filter is set to weekly, 
              then the selected week will be displayed. */}
          <HStack spacing={4} align="stretch" flexWrap="wrap">
            {/* Sub Box A Holds the Following:
                Stat Cards
                Ticket Distribution per Division
                */}
            <VStack spacing={4} align="stretch" flex="1" minW="300px">
              {/* Stat Cards - Must be Displayed in a 2x2 Box */}
              <Card>
                <CardHeader pb={2}>
                  <Heading size="sm">Ticket Performance Snapshot</Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                    {SUMMARY_CARDS.map((card) => (
                      <Card key={card.key} variant="outline">
                        <CardBody>
                          <Stat>
                            <StatLabel>{card.label}</StatLabel>
                            <StatNumber>
                              {formatStatCardValue(summaryCardValues[card.key], card.valueType)}
                            </StatNumber>
                          </Stat>
                        </CardBody>
                      </Card>
                    ))}
                  </Grid>
                </CardBody>
              </Card>

              {/* Ticket Distribution per Division - Will be displayed in a stacked chart.
                  The header of this table will contain the Title, and a dropdown for filtering
                  between weekly or monthly data */}
              <Card flex="1" display="flex" flexDirection="column">
                <CardHeader>
                  <Heading size="sm">Ticket Distribution per Division</Heading>
                </CardHeader>
                <CardBody flex="1" display="flex">
                  <Box h="100%" minH="260px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={divisionData} {...TIGHT_BAR_CHART_PROPS}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="division" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="incident" stackId="division" fill="#3182ce" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="service" stackId="division" fill="#805ad5" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="request" stackId="division" fill="#38a169" radius={[2, 2, 0, 0]} />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>
            </VStack>

            {/* Sub Box B Holds the Following:
                Date Range Filter
                Ticket Resolution per Type and SLA
                Ticket Resolution Trend
                */}
            <VStack spacing={4} align="stretch" flex="1" minW="300px">
              {/* Date Range Filter - Will be displayed in a horizontal box with a start 
                  and end date picker */}
              <Card>
                <CardHeader pb={2}>
                  <Heading size="sm">Date Range Filter</Heading>
                </CardHeader>
                <CardBody pt={0}>
                  <HStack spacing={3} align="end" flexWrap="wrap">
                    <FormControl
                      display="flex"
                      flexDirection="column"
                      maxW="fit-content"
                      maxH="fit-content"
                    >
                      <FormLabel fontSize="sm" mb={1}>
                        Range type
                      </FormLabel>

                      <Select
                        size="sm"
                        value={dateRangeGranularity}
                        onChange={(e) =>
                          setDateRangeGranularity(
                            e.target.value as DateRangeGranularity
                          )
                        }
                      >
                        <option value="monthly">Monthly</option>
                        <option value="weekly">Weekly</option>
                        <option value="custom">Custom</option>
                      </Select>
                    </FormControl>
                    {dateRangeGranularity !== 'custom' ? (
                      <FormControl display="flex" flexDirection="column" maxW="fit-content" maxH="fit-content">
                        <FormLabel fontSize="sm" mb={1}>
                          {dateRangeGranularity === 'monthly' ? 'Select month' : 'Select date'}
                        </FormLabel>
                        <Box position="relative" w={displayedDateInputWidth}>
                          <Input
                            type="text"
                            size="sm"
                            value={displayedDateRangeValue}
                            readOnly
                            w="100%"
                            pr={10}
                            cursor="pointer"
                            onClick={openDatePicker}
                            onFocus={openDatePicker}
                          />
                          <Input
                            ref={datePickerInputRef}
                            position="absolute"
                            inset={0}
                            type="date"
                            size="sm"
                            value={selectedDateValue}
                            onChange={(e) => setSelectedDateValue(e.target.value)}
                            opacity={0}
                            pointerEvents="none"
                            tabIndex={-1}
                            aria-label={dateRangeGranularity === 'monthly' ? 'Select month' : 'Select week'}
                          />
                          <Icon
                            position="absolute"
                            right={3}
                            top="50%"
                            transform="translateY(-50%)"
                            boxSize={4}
                            color="gray.500"
                            pointerEvents="none"
                            viewBox="0 0 24 24"
                            zIndex={2}
                          >
                            <Calendar />
                          </Icon>
                        </Box>
                      </FormControl>
                    ) : (
                      <>
                        <FormControl display="flex" flexDirection="column" maxW="fit-content" maxH="fit-content">
                          <FormLabel fontSize="sm" mb={1}>From</FormLabel>
                          <Box position="relative">
                            <Input
                              type="text"
                              size="sm"
                              value={formatVisibleDateValue(customDateFrom)}
                              readOnly
                              pr={10}
                              cursor="pointer"
                              onClick={openCustomFromPicker}
                              onFocus={openCustomFromPicker}
                            />
                            <Input
                              ref={customFromInputRef}
                              position="absolute"
                              inset={0}
                              type="date"
                              size="sm"
                              value={customDateFrom}
                              onChange={(e) => setCustomDateFrom(e.target.value)}
                              opacity={0}
                              pointerEvents="none"
                              tabIndex={-1}
                              aria-label="Select from date"
                            />
                            <Icon
                              position="absolute"
                              right={3}
                              top="50%"
                              transform="translateY(-50%)"
                              boxSize={4}
                              color="gray.500"
                              pointerEvents="none"
                              viewBox="0 0 24 24"
                              zIndex={2}
                            >
                              <path
                                fill="currentColor"
                                d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1ha3 3 0 0 1 3 3v11a4 4 0 0 1-41 4H6a4 4 0 0 1-4-4V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8ZM5 6a1 1 0 0 0-1 1v1h16V7a1 1 0 0 0-1-1H5Z"
                              />
                            </Icon>
                          </Box>
                        </FormControl>
                        <FormControl display="flex" flexDirection="column" maxW="fit-content" maxH="fit-content">
                          <FormLabel fontSize="sm" mb={1}>To</FormLabel>
                          <Box position="relative">
                            <Input
                              type="text"
                              size="sm"
                              value={formatVisibleDateValue(customDateTo)}
                              readOnly
                              pr={10}
                              cursor="pointer"
                              onClick={openCustomToPicker}
                              onFocus={openCustomToPicker}
                            />
                            <Input
                              ref={customToInputRef}
                              position="absolute"
                              inset={0}
                              type="date"
                              size="sm"
                              value={customDateTo}
                              onChange={(e) => setCustomDateTo(e.target.value)}
                              opacity={0}
                              pointerEvents="none"
                              tabIndex={-1}
                              aria-label="Select to date"
                            />
                            <Icon
                              position="absolute"
                              right={3}
                              top="50%"
                              transform="translateY(-50%)"
                              boxSize={4}
                              color="gray.500"
                              pointerEvents="none"
                              viewBox="0 0 24 24"
                              zIndex={2}
                            >
                              <path
                                fill="currentColor"
                                d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1ha3 3 0 0 1 3 3v11a4 4 0 0 1-41 4H6a4 4 0 0 1-4-4V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8ZM5 6a1 1 0 0 0-1 1v1h16V7a1 1 0 0 0-1-1H5Z"
                              />
                            </Icon>
                          </Box>
                        </FormControl>
                      </>
                    )}
                  </HStack>
                </CardBody>
              </Card>

              {/* Ticket Resolution per Type and SLA - Will be displayed in a stacked bar chart.
                  X-axis will display months and Y-axis the number of tickets. */}
              <Card>
                <CardHeader>
                  <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                    <Heading size="sm">Ticket Resolution per Type and SLA</Heading>
                    <Select
                      size="sm"
                      maxW="220px"
                      value={resolutionServiceTypeFilter}
                      onChange={(e) => setResolutionServiceTypeFilter(e.target.value as ResolutionServiceTypeFilter)}
                    >
                      <option value="all">All Tickets</option>
                      <option value="incident">Incident</option>
                      <option value="inquiry">Inquiry</option>
                      <option value="request">Request</option>
                    </Select>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Box h="240px">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={resolutionByTypeSlaData} {...TIGHT_BAR_CHART_PROPS}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="withinSla" stackId="sla" fill="#48bb78" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="nearingSla" stackId="sla" fill="#ecc94b" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="breached" stackId="sla" fill="#f56565" radius={[2, 2, 0, 0]} />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>

              {/* Ticket Resolution Trend - Will be displayed in a line chart.
                  The header of this table will contain the Title, and a dropdown for filtering
                  between weekly or monthly data */}
              <Card>
                <CardHeader>
                  <Heading size="sm">Ticket Resolution Trend</Heading>
                </CardHeader>
                <CardBody>
                  <Box h="240px">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsLineChart data={resolutionTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="current" stroke="#2b6cb0" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="previous" stroke="#718096" strokeWidth={2} dot={false} />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardBody>
              </Card>
            </VStack>
          </HStack>

          {/* Main Box B Holds the Following:
              Ticket Trend of the Month per week and Type
              MTTR of Each Ticket Separated via Time and Category
              */}
          <HStack spacing={4} align="stretch" flexWrap="wrap">
            {/* Ticket Trend of the Month per week and Type - Will be displayed in a stacked bar chart.
                  The header of this table will contain the Title, and a dropdown for filtering
                  between weekly or monthly data */}
            <Card flex="1" minW="320px">
              <CardHeader>
                <Heading size="sm">Ticket Trend of the Month (Week x Type)</Heading>
              </CardHeader>
              <CardBody>
                <Box h="240px">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={ticketTrendByWeekTypeData} {...TIGHT_BAR_CHART_PROPS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="incident" stackId="type" fill="#3182ce" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="service" stackId="type" fill="#805ad5" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="request" stackId="type" fill="#38a169" radius={[2, 2, 0, 0]} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>

            {/* MTTR of Each Ticket Separated via Time and Category - Will be displayed in a stacked bar chart.
                  The header of this table will contain the Title, and a dropdown for filtering
                  between weekly or monthly data */}
            <Card flex="1" minW="320px">
              <CardHeader>
                <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                  <Heading size="sm">MTTR by Time and Category</Heading>
                  <HStack spacing={2}>
                    <Select
                      size="sm"
                      maxW="220px"
                      value={mttrServiceTypeId ?? ''}
                      onChange={(e) => setMttrServiceTypeId(e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">All service types</option>
                      {serviceTypes.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </Select>
                  </HStack>
                </Flex>
              </CardHeader>
              <CardBody>
                <VStack spacing={2} align="stretch" h="240px">
                  <Text fontSize="sm" color="gray.500">Average MTTR: {formatMTTR(ticketStats.net_recovery_minutes)}</Text>
                  <Box flex="1">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={mttrByCategoryData} {...TIGHT_BAR_CHART_PROPS}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="under4h" stackId="mttr" fill="#38b2ac" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="from4hTo1d" stackId="mttr" fill="#dd6b20" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="over1d" stackId="mttr" fill="#e53e3e" radius={[2, 2, 0, 0]} />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </HStack>

          {/* Main Box C Holds the Following:
              Number of Items per SubCategory
              */}
          <HStack spacing={4} align="stretch" flexWrap="wrap">
            {/* Number of Items per SubCategory - Will be displayed in a stacked bar chart.
                  The header of this table will contain the Title, a dropdown for filtering
                  between weekly or monthly data, and another dropdown for filtering by category */}
            <Card flex="1" minW="320px">
              <CardHeader>
                <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                  <Heading size="sm">Number of Items per SubCategory</Heading>
                  <Select size="sm" maxW="180px" value={subCategoryFilter} onChange={(e) => setSubCategoryFilter(e.target.value as 'all' | 'service' | 'incident' | 'request')}>
                    <option value="all">All categories</option>
                    <option value="service">Service</option>
                    <option value="incident">Incident</option>
                    <option value="request">Request</option>
                  </Select>
                </Flex>
              </CardHeader>
              <CardBody>
                <Box h="260px">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={itemsPerSubCategoryData} {...TIGHT_BAR_CHART_PROPS}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="subCategory" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="service" stackId="subCategory" fill="#4299e1" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="incident" stackId="subCategory" fill="#f6ad55" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="request" stackId="subCategory" fill="#68d391" radius={[2, 2, 0, 0]} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </Box>
              </CardBody>
            </Card>
          </HStack>

        </VStack>
      </Box>
    </AdminLayout>
  )
}

